import {
  SECRET_SHAPED_PAYLOAD_KEYS,
  ReportValidationError,
  assertReportWellFormed,
  type ReportEnvelope,
} from '../streams/report.js';

export const WORKER_EVIDENCE_BUNDLE_VERSION = 1 as const;
export const SUPERVISOR_REPORT_VERSION = 1 as const;

export const WORKER_EVIDENCE_LIMITS = {
  structuredReports: 16,
  chatEvidence: 16,
  blockers: 12,
  questions: 12,
} as const;

export const SUPERVISOR_REPORT_LIMITS = {
  workers: 16,
  evidenceRefs: 48,
  facts: 24,
  conflicts: 12,
  summaryChars: 1_000,
  statementChars: 1_200,
  recommendationChars: 1_200,
  questionChars: 1_200,
  serializedChars: 16_000,
} as const;

export const TRUTH_STATES = ['Known', 'Unknown', 'Unavailable'] as const;
export type TruthState = (typeof TRUTH_STATES)[number];

export const CONFIDENCE_STATES = ['high', 'medium', 'low', 'unknown'] as const;
export type ConfidenceState = (typeof CONFIDENCE_STATES)[number];

export const EVIDENCE_SOURCES = [
  'chat',
  'report',
  'task',
  'run',
  'result',
  'handoff',
  'runtime',
  'terminal',
] as const;
export type EvidenceSource = (typeof EVIDENCE_SOURCES)[number];

export type EvidenceTruth<T> =
  | { readonly state: 'Known'; readonly value: T }
  | { readonly state: 'Unknown'; readonly reason?: string }
  | { readonly state: 'Unavailable'; readonly reason?: string };

export interface EvidenceReference {
  readonly id: string;
  readonly source: EvidenceSource;
  readonly workerId: string;
  readonly sessionId?: string;
  readonly taskId?: string;
  readonly runId?: string;
  readonly observedAt: string;
}

export interface WorkerEvidenceIdentity {
  readonly workerId: string;
  readonly sessionId: EvidenceTruth<string>;
  readonly taskId: EvidenceTruth<string>;
  readonly runId: EvidenceTruth<string>;
}

export interface WorkerStructuredReportEvidence {
  readonly reference: EvidenceReference;
  readonly report: ReportEnvelope;
}

export interface WorkerEvidenceBundle {
  readonly bundleVersion: typeof WORKER_EVIDENCE_BUNDLE_VERSION;
  readonly identity: WorkerEvidenceIdentity;
  readonly structuredReports: readonly WorkerStructuredReportEvidence[];
  readonly chatEvidence: readonly EvidenceReference[];
  readonly result: EvidenceTruth<EvidenceReference>;
  readonly handoff: EvidenceTruth<EvidenceReference>;
  readonly blockers: readonly EvidenceReference[];
  readonly questions: readonly EvidenceReference[];
  readonly runtime: EvidenceTruth<string>;
  readonly terminal: EvidenceTruth<string>;
  readonly collectedAt: string;
}

export interface SupervisorIdentity {
  readonly id: string;
  readonly role: string;
}

export interface SupervisorFact {
  readonly id: string;
  readonly statement: string;
  readonly truth: TruthState;
  readonly confidence: ConfidenceState;
  readonly evidenceRefIds: readonly string[];
  readonly workerIds: readonly string[];
}

export interface SupervisorConflict {
  readonly id: string;
  readonly statement: string;
  readonly factIds: readonly string[];
  readonly evidenceRefIds: readonly string[];
  readonly workerIds: readonly string[];
}

export interface SupervisorReportInput {
  readonly supervisor: SupervisorIdentity;
  readonly workers: readonly WorkerEvidenceIdentity[];
  readonly evidenceRefs: readonly EvidenceReference[];
  readonly facts: readonly SupervisorFact[];
  readonly conflicts: readonly SupervisorConflict[];
  readonly recommendation: string | null;
  readonly question: string | null;
  readonly truth: TruthState;
  readonly confidence: ConfidenceState;
  readonly summary: string;
  readonly createdAt: string;
}

export interface SupervisorReport extends SupervisorReportInput {
  readonly reportVersion: typeof SUPERVISOR_REPORT_VERSION;
  readonly correlationId: string;
  readonly executionAuthority: false;
}

export const SUPERVISOR_REPORT_CONSUMERS = ['overseer', 'ux', 'core'] as const;
export type SupervisorReportConsumer = (typeof SUPERVISOR_REPORT_CONSUMERS)[number];

export const SUPERVISOR_CLARIFICATION_ROUTE = {
  transport: 'worker_chat_turn',
  reuseOnly: true,
  onBusy: 'wait',
  createsTask: false,
  writesTerminal: false,
  restartsSession: false,
  switchesRuntimeOrModel: false,
  executionAuthority: false,
} as const;

const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function assertExactKeys(value: Record<string, unknown>, allowed: readonly string[], field: string): void {
  const allowedSet = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowedSet.has(key)) {
      throw new ReportValidationError(`${field} contains unsupported field '${key}'`);
    }
  }
}

function assertNoSecretShapedKeys(value: unknown, path = 'report'): void {
  if (Array.isArray(value)) {
    value.forEach((entry, index) => assertNoSecretShapedKeys(entry, `${path}[${index}]`));
    return;
  }
  if (!isRecord(value)) return;
  for (const [key, nested] of Object.entries(value)) {
    const normalized = key.toLowerCase();
    if (SECRET_SHAPED_PAYLOAD_KEYS.some((term) => normalized.includes(term))) {
      throw new ReportValidationError(`${path} field '${key}' is secret-shaped; report data must remain non-secret`);
    }
    assertNoSecretShapedKeys(nested, `${path}.${key}`);
  }
}

function assertBoundedText(value: unknown, field: string, maxChars: number): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new ReportValidationError(`${field} must be a non-blank string`);
  }
  if (value.length > maxChars) {
    throw new ReportValidationError(`${field} exceeds ${maxChars} characters`);
  }
}

function assertOptionalBoundedText(value: unknown, field: string, maxChars: number): asserts value is string | null {
  if (value === null) return;
  assertBoundedText(value, field, maxChars);
}

function assertIsoTimestamp(value: unknown, field: string): asserts value is string {
  if (
    typeof value !== 'string' ||
    !ISO_TIMESTAMP_PATTERN.test(value) ||
    Number.isNaN(new Date(value).getTime())
  ) {
    throw new ReportValidationError(`${field} must be an ISO timestamp`);
  }
}

function assertTruthState(value: unknown, field: string): asserts value is TruthState {
  if (!TRUTH_STATES.includes(value as TruthState)) {
    throw new ReportValidationError(`${field} must be Known, Unknown, or Unavailable`);
  }
}

function assertConfidenceState(value: unknown, field: string): asserts value is ConfidenceState {
  if (!CONFIDENCE_STATES.includes(value as ConfidenceState)) {
    throw new ReportValidationError(`${field} has an unsupported confidence state`);
  }
}

function assertStringArray(
  value: unknown,
  field: string,
  maxItems: number,
  options: { readonly minItems?: number; readonly unique?: boolean } = {},
): asserts value is string[] {
  if (!Array.isArray(value)) {
    throw new ReportValidationError(`${field} must be an array`);
  }
  const minItems = options.minItems ?? 0;
  if (value.length < minItems || value.length > maxItems) {
    throw new ReportValidationError(`${field} must contain ${minItems}-${maxItems} items`);
  }
  for (const [index, item] of value.entries()) {
    assertBoundedText(item, `${field}[${index}]`, 200);
  }
  if (options.unique !== false && new Set(value).size !== value.length) {
    throw new ReportValidationError(`${field} must not contain duplicate values`);
  }
}

function assertEvidenceReference(value: unknown, field: string): asserts value is EvidenceReference {
  if (!isRecord(value)) throw new ReportValidationError(`${field} must be an evidence reference`);
  assertExactKeys(value, ['id', 'source', 'workerId', 'sessionId', 'taskId', 'runId', 'observedAt'], field);
  assertBoundedText(value.id, `${field}.id`, 200);
  if (!EVIDENCE_SOURCES.includes(value.source as EvidenceSource)) {
    throw new ReportValidationError(`${field}.source is unsupported`);
  }
  assertBoundedText(value.workerId, `${field}.workerId`, 200);
  for (const optional of ['sessionId', 'taskId', 'runId'] as const) {
    if (value[optional] !== undefined) assertBoundedText(value[optional], `${field}.${optional}`, 200);
  }
  assertIsoTimestamp(value.observedAt, `${field}.observedAt`);
}

function assertEvidenceArray(value: unknown, field: string, maxItems: number): asserts value is EvidenceReference[] {
  if (!Array.isArray(value) || value.length > maxItems) {
    throw new ReportValidationError(`${field} must be an array with at most ${maxItems} items`);
  }
  value.forEach((entry, index) => assertEvidenceReference(entry, `${field}[${index}]`));
  const ids = value.map((entry) => entry.id);
  if (new Set(ids).size !== ids.length) {
    throw new ReportValidationError(`${field} must not contain duplicate evidence ids`);
  }
}

function assertStructuredReportArray(
  value: unknown,
  field: string,
  maxItems: number,
): asserts value is WorkerStructuredReportEvidence[] {
  if (!Array.isArray(value) || value.length > maxItems) {
    throw new ReportValidationError(`${field} must be an array with at most ${maxItems} items`);
  }

  const ids: string[] = [];
  value.forEach((entry, index) => {
    const entryField = `${field}[${index}]`;
    if (!isRecord(entry)) throw new ReportValidationError(`${entryField} must be an object`);
    assertExactKeys(entry, ['reference', 'report'], entryField);
    assertEvidenceReference(entry.reference, `${entryField}.reference`);
    if (entry.reference.source !== 'report') {
      throw new ReportValidationError(`${entryField}.reference.source must be report`);
    }
    assertReportWellFormed(entry.report as ReportEnvelope);
    ids.push(entry.reference.id);
  });

  if (new Set(ids).size !== ids.length) {
    throw new ReportValidationError(`${field} must not contain duplicate evidence ids`);
  }
}

function assertEvidenceTruth<T>(
  value: unknown,
  field: string,
  validateKnown: (known: unknown, knownField: string) => asserts known is T,
): asserts value is EvidenceTruth<T> {
  if (!isRecord(value)) throw new ReportValidationError(`${field} must be a truth wrapper`);
  if (value.state === 'Known') {
    assertExactKeys(value, ['state', 'value'], field);
    validateKnown(value.value, `${field}.value`);
    return;
  }
  if (value.state === 'Unknown' || value.state === 'Unavailable') {
    assertExactKeys(value, ['state', 'reason'], field);
    if (value.reason !== undefined) assertBoundedText(value.reason, `${field}.reason`, 500);
    return;
  }
  throw new ReportValidationError(`${field}.state must be Known, Unknown, or Unavailable`);
}

function assertKnownText(value: unknown, field: string): asserts value is string {
  assertBoundedText(value, field, 1_000);
}

function assertWorkerIdentity(value: unknown, field: string): asserts value is WorkerEvidenceIdentity {
  if (!isRecord(value)) throw new ReportValidationError(`${field} must be a Worker identity`);
  assertExactKeys(value, ['workerId', 'sessionId', 'taskId', 'runId'], field);
  assertBoundedText(value.workerId, `${field}.workerId`, 200);
  assertEvidenceTruth(value.sessionId, `${field}.sessionId`, assertKnownText);
  assertEvidenceTruth(value.taskId, `${field}.taskId`, assertKnownText);
  assertEvidenceTruth(value.runId, `${field}.runId`, assertKnownText);
}

function assertSupervisorIdentity(value: unknown): asserts value is SupervisorIdentity {
  if (!isRecord(value)) throw new ReportValidationError('supervisor must be an object');
  assertExactKeys(value, ['id', 'role'], 'supervisor');
  assertBoundedText(value.id, 'supervisor.id', 200);
  assertBoundedText(value.role, 'supervisor.role', 200);
}

function assertSupervisorFact(value: unknown, index: number): asserts value is SupervisorFact {
  const field = `facts[${index}]`;
  if (!isRecord(value)) throw new ReportValidationError(`${field} must be an object`);
  assertExactKeys(value, ['id', 'statement', 'truth', 'confidence', 'evidenceRefIds', 'workerIds'], field);
  assertBoundedText(value.id, `${field}.id`, 200);
  assertBoundedText(value.statement, `${field}.statement`, SUPERVISOR_REPORT_LIMITS.statementChars);
  assertTruthState(value.truth, `${field}.truth`);
  assertConfidenceState(value.confidence, `${field}.confidence`);
  assertStringArray(value.evidenceRefIds, `${field}.evidenceRefIds`, SUPERVISOR_REPORT_LIMITS.evidenceRefs);
  assertStringArray(value.workerIds, `${field}.workerIds`, SUPERVISOR_REPORT_LIMITS.workers);
  if (value.truth === 'Known' && value.evidenceRefIds.length === 0) {
    throw new ReportValidationError(`${field} Known facts require at least one evidence reference`);
  }
}

function assertSupervisorConflict(value: unknown, index: number): asserts value is SupervisorConflict {
  const field = `conflicts[${index}]`;
  if (!isRecord(value)) throw new ReportValidationError(`${field} must be an object`);
  assertExactKeys(value, ['id', 'statement', 'factIds', 'evidenceRefIds', 'workerIds'], field);
  assertBoundedText(value.id, `${field}.id`, 200);
  assertBoundedText(value.statement, `${field}.statement`, SUPERVISOR_REPORT_LIMITS.statementChars);
  assertStringArray(value.factIds, `${field}.factIds`, SUPERVISOR_REPORT_LIMITS.facts, { minItems: 2 });
  assertStringArray(
    value.evidenceRefIds,
    `${field}.evidenceRefIds`,
    SUPERVISOR_REPORT_LIMITS.evidenceRefs,
    { minItems: 2 },
  );
  assertStringArray(value.workerIds, `${field}.workerIds`, SUPERVISOR_REPORT_LIMITS.workers, { minItems: 2 });
}

function assertUniqueIds(values: readonly { readonly id: string }[], field: string): void {
  const ids = values.map((value) => value.id);
  if (new Set(ids).size !== ids.length) {
    throw new ReportValidationError(`${field} must use unique ids`);
  }
}

function assertReferencesResolve(input: SupervisorReportInput): void {
  const evidenceIds = new Set(input.evidenceRefs.map((entry) => entry.id));
  const workerIds = new Set(input.workers.map((entry) => entry.workerId));
  const factIds = new Set(input.facts.map((entry) => entry.id));

  for (const [index, fact] of input.facts.entries()) {
    for (const ref of fact.evidenceRefIds) {
      if (!evidenceIds.has(ref)) throw new ReportValidationError(`facts[${index}] references missing evidence '${ref}'`);
    }
    for (const workerId of fact.workerIds) {
      if (!workerIds.has(workerId)) throw new ReportValidationError(`facts[${index}] references missing Worker '${workerId}'`);
    }
  }

  for (const [index, conflict] of input.conflicts.entries()) {
    for (const factId of conflict.factIds) {
      if (!factIds.has(factId)) throw new ReportValidationError(`conflicts[${index}] references missing fact '${factId}'`);
    }
    for (const ref of conflict.evidenceRefIds) {
      if (!evidenceIds.has(ref)) {
        throw new ReportValidationError(`conflicts[${index}] references missing evidence '${ref}'`);
      }
    }
    for (const workerId of conflict.workerIds) {
      if (!workerIds.has(workerId)) {
        throw new ReportValidationError(`conflicts[${index}] references missing Worker '${workerId}'`);
      }
    }
  }
}

function assertSupervisorReportInput(input: SupervisorReportInput): void {
  assertNoSecretShapedKeys(input);
  if (!isRecord(input)) throw new ReportValidationError('Supervisor report input must be an object');
  assertExactKeys(
    input,
    [
      'supervisor',
      'workers',
      'evidenceRefs',
      'facts',
      'conflicts',
      'recommendation',
      'question',
      'truth',
      'confidence',
      'summary',
      'createdAt',
    ],
    'Supervisor report input',
  );
  assertSupervisorIdentity(input.supervisor);

  if (!Array.isArray(input.workers) || input.workers.length === 0 || input.workers.length > SUPERVISOR_REPORT_LIMITS.workers) {
    throw new ReportValidationError(`workers must contain 1-${SUPERVISOR_REPORT_LIMITS.workers} items`);
  }
  input.workers.forEach((worker, index) => assertWorkerIdentity(worker, `workers[${index}]`));
  const workerIds = input.workers.map((worker) => worker.workerId);
  if (new Set(workerIds).size !== workerIds.length) {
    throw new ReportValidationError('workers must not contain duplicate Worker ids');
  }

  assertEvidenceArray(input.evidenceRefs, 'evidenceRefs', SUPERVISOR_REPORT_LIMITS.evidenceRefs);
  if (!Array.isArray(input.facts) || input.facts.length > SUPERVISOR_REPORT_LIMITS.facts) {
    throw new ReportValidationError(`facts must contain at most ${SUPERVISOR_REPORT_LIMITS.facts} items`);
  }
  input.facts.forEach((fact, index) => assertSupervisorFact(fact, index));
  assertUniqueIds(input.facts, 'facts');

  if (!Array.isArray(input.conflicts) || input.conflicts.length > SUPERVISOR_REPORT_LIMITS.conflicts) {
    throw new ReportValidationError(`conflicts must contain at most ${SUPERVISOR_REPORT_LIMITS.conflicts} items`);
  }
  input.conflicts.forEach((conflict, index) => assertSupervisorConflict(conflict, index));
  assertUniqueIds(input.conflicts, 'conflicts');

  assertOptionalBoundedText(input.recommendation, 'recommendation', SUPERVISOR_REPORT_LIMITS.recommendationChars);
  assertOptionalBoundedText(input.question, 'question', SUPERVISOR_REPORT_LIMITS.questionChars);
  if (input.recommendation === null && input.question === null) {
    throw new ReportValidationError('Supervisor report requires a recommendation or a question');
  }

  assertTruthState(input.truth, 'truth');
  assertConfidenceState(input.confidence, 'confidence');
  assertBoundedText(input.summary, 'summary', SUPERVISOR_REPORT_LIMITS.summaryChars);
  assertIsoTimestamp(input.createdAt, 'createdAt');
  assertReferencesResolve(input);
}

function truthFingerprint(value: EvidenceTruth<string>): readonly [string, string] {
  return value.state === 'Known'
    ? [value.state, value.value]
    : [value.state, value.reason ?? ''];
}

function normalizedWorker(worker: WorkerEvidenceIdentity): readonly unknown[] {
  return [
    worker.workerId,
    truthFingerprint(worker.sessionId),
    truthFingerprint(worker.taskId),
    truthFingerprint(worker.runId),
  ];
}

function normalizedEvidence(ref: EvidenceReference): readonly unknown[] {
  return [
    ref.id,
    ref.source,
    ref.workerId,
    ref.sessionId ?? '',
    ref.taskId ?? '',
    ref.runId ?? '',
    ref.observedAt,
  ];
}

function correlationMaterial(input: SupervisorReportInput): string {
  const workers = [...input.workers].sort((a, b) => a.workerId.localeCompare(b.workerId)).map(normalizedWorker);
  const evidence = [...input.evidenceRefs].sort((a, b) => a.id.localeCompare(b.id)).map(normalizedEvidence);
  const facts = [...input.facts]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((fact) => [
      fact.id,
      fact.statement,
      fact.truth,
      fact.confidence,
      [...fact.evidenceRefIds].sort(),
      [...fact.workerIds].sort(),
    ]);
  const conflicts = [...input.conflicts]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((conflict) => [
      conflict.id,
      conflict.statement,
      [...conflict.factIds].sort(),
      [...conflict.evidenceRefIds].sort(),
      [...conflict.workerIds].sort(),
    ]);

  return JSON.stringify({
    supervisor: [input.supervisor.id, input.supervisor.role],
    workers,
    evidence,
    facts,
    conflicts,
    recommendation: input.recommendation,
    question: input.question,
    truth: input.truth,
    confidence: input.confidence,
    summary: input.summary,
  });
}

function fnv1a32(value: string, seed: number): string {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function supervisorReportCorrelationId(input: SupervisorReportInput): string {
  assertSupervisorReportInput(input);
  const material = correlationMaterial(input);
  return `supervisor-report-v1:${fnv1a32(material, 2_166_136_261)}${fnv1a32(material, 3_335_557_777)}`;
}

export function createSupervisorReport(input: SupervisorReportInput): SupervisorReport {
  assertSupervisorReportInput(input);
  const report: SupervisorReport = {
    reportVersion: SUPERVISOR_REPORT_VERSION,
    ...input,
    correlationId: supervisorReportCorrelationId(input),
    executionAuthority: false,
  };
  assertSupervisorReportWellFormed(report);
  return report;
}

export function assertSupervisorReportWellFormed(report: SupervisorReport): void {
  assertNoSecretShapedKeys(report);
  if (!isRecord(report)) throw new ReportValidationError('Supervisor report must be an object');
  assertExactKeys(
    report,
    [
      'reportVersion',
      'supervisor',
      'workers',
      'evidenceRefs',
      'facts',
      'conflicts',
      'recommendation',
      'question',
      'truth',
      'confidence',
      'summary',
      'createdAt',
      'correlationId',
      'executionAuthority',
    ],
    'Supervisor report',
  );
  if (report.reportVersion !== SUPERVISOR_REPORT_VERSION) {
    throw new ReportValidationError('Supervisor report version is unsupported');
  }
  if (report.executionAuthority !== false) {
    throw new ReportValidationError('Supervisor reports are advisory and cannot carry execution authority');
  }

  const input: SupervisorReportInput = {
    supervisor: report.supervisor,
    workers: report.workers,
    evidenceRefs: report.evidenceRefs,
    facts: report.facts,
    conflicts: report.conflicts,
    recommendation: report.recommendation,
    question: report.question,
    truth: report.truth,
    confidence: report.confidence,
    summary: report.summary,
    createdAt: report.createdAt,
  };
  assertSupervisorReportInput(input);

  const expected = supervisorReportCorrelationId(input);
  if (report.correlationId !== expected) {
    throw new ReportValidationError('Supervisor report correlationId does not match its deterministic content');
  }

  const serialized = JSON.stringify(report);
  if (serialized.length > SUPERVISOR_REPORT_LIMITS.serializedChars) {
    throw new ReportValidationError(
      `Supervisor report exceeds ${SUPERVISOR_REPORT_LIMITS.serializedChars} serialized characters`,
    );
  }
}

export function supervisorReportToEnvelope(report: SupervisorReport, streamId: string): ReportEnvelope {
  assertSupervisorReportWellFormed(report);
  assertBoundedText(streamId, 'streamId', 200);
  const detail = JSON.stringify(report);
  const envelope: ReportEnvelope = {
    streamId,
    reportKind: 'supervisor_synthesis',
    payloadVersion: SUPERVISOR_REPORT_VERSION,
    summary: report.summary,
    payload: {
      protocol: 'supervisor_report',
      reportVersion: SUPERVISOR_REPORT_VERSION,
      correlationId: report.correlationId,
      supervisorId: report.supervisor.id,
      supervisorRole: report.supervisor.role,
      truth: report.truth,
      confidence: report.confidence,
      workerCount: report.workers.length,
      evidenceCount: report.evidenceRefs.length,
      factCount: report.facts.length,
      conflictCount: report.conflicts.length,
      executionAuthority: false,
      detail,
    },
    createdAt: report.createdAt,
  };
  assertReportWellFormed(envelope);
  return envelope;
}

export function supervisorReportFromEnvelope(envelope: ReportEnvelope): SupervisorReport {
  assertReportWellFormed(envelope);
  if (envelope.reportKind !== 'supervisor_synthesis' || envelope.payloadVersion !== SUPERVISOR_REPORT_VERSION) {
    throw new ReportValidationError('envelope is not a Supervisor report v1');
  }
  if (envelope.payload.protocol !== 'supervisor_report' || typeof envelope.payload.detail !== 'string') {
    throw new ReportValidationError('Supervisor report envelope payload is incomplete');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(envelope.payload.detail);
  } catch {
    throw new ReportValidationError('Supervisor report envelope detail must contain valid JSON');
  }
  assertSupervisorReportWellFormed(parsed as SupervisorReport);
  return parsed as SupervisorReport;
}
